import React from 'react';
import classnames from 'classnames';
import Schedule from './Schedule';
import {
  EnvironmentOutlined,
  ExportOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  StarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Rate } from 'antd';
import { OrderMethod } from '../../../../../types/constants';
import useBusinessHours from '../../../../../hooks/availability/useBusinessHours';
import { SectionProps } from '../../types';
import ClickToCopy from '../../../../helpers/ClickToCopy';
import { Stringable } from '../../../../../services/utils/Stringable';
import { SidebarSectionEnum } from '../../enums';
import FranchiseRestaurants from './FranchiseRestaurants';

type Props = SectionProps & {
  onStoreSelected: (id: string) => Promise<void>;
};

const RestaurantInfo = ({ data, onStoreSelected, ...rest }: Props) => {
  const { isOrderMethodOpen } = useBusinessHours(data);
  const isOpen = isOrderMethodOpen(OrderMethod.Pickup);
  const orderMethodOpenLabel = isOpen ? 'Open' : 'Closed';
  const googleMapLink = `https://www.google.com/maps/place/?q=place_id:${data.google_place.id}`;

  return (
    <section {...rest} id={SidebarSectionEnum.RestaurantInfo}>
      <div className="flex items-center justify-between">
        <h4 className="text-md font-semibold text-slate-700">{data.name}</h4>
        <div className="flex items-center gap-x-2">
          <span
            className={classnames('size-3 rounded-full', {
              'bg-primary': isOpen,
              'bg-danger': !isOpen,
            })}
          />
          <p
            className={classnames('text-sm uppercase font-medium', {
              'text-primary': isOpen,
              'text-danger': !isOpen,
            })}
          >
            {orderMethodOpenLabel}
          </p>
        </div>
      </div>
      <FranchiseRestaurants data={data} onStoreSelected={onStoreSelected} />
      <Schedule data={data} />
      <p className="text-sm text-slate-600 flex items-center">
        <UserOutlined className="text-md mr-2" />
        Owner: {data.contacts.name}
      </p>
      <p className="text-sm text-slate-600 flex items-center">
        <PhoneOutlined className="mr-2 text-md rotate-90" />
        {data.contacts.phone_number}
        <ClickToCopy value={data.contacts.phone_number} className="ml-auto" />
      </p>
      <p className="text-sm text-slate-600 flex items-center">
        <MailOutlined className="text-md mr-2" />
        {data.contacts.email}
      </p>
      {data.links.website && (
        <p className="text-sm text-slate-600 flex items-center">
          <GlobalOutlined className="mr-2 text-md" />
          {data.links.website}
          <ExportOutlined
            className="ml-auto"
            onClick={() =>
              window.open(
                new Stringable(data.links.website).start('https://').toString(),
                '_blank',
                'noopener noreferrer',
              )
            }
          />
        </p>
      )}
      <p className="text-sm text-slate-600 flex">
        <EnvironmentOutlined className="mr-2 text-md" />
        {data.location.formatted_address}
        <ExportOutlined
          className="ml-auto"
          onClick={() => window.open(googleMapLink, '_blank', 'noopener noreferrer')}
        />
      </p>
      <div className="text-sm text-slate-600 flex space-x-2 items-center">
        <StarOutlined className="text-md text-amber-400" />
        <Rate
          className="text-sm text-amber-400"
          disabled
          allowHalf
          value={data.google_place.rating}
        />
        <p className="text-sm font-medium">{`${data.google_place.rating} (${data.google_place.rates_count})`}</p>
      </div>
    </section>
  );
};

export default RestaurantInfo;
